import { useState } from "react";
import { Link } from "react-router-dom";
import { Euro, Droplet, Fuel, Receipt } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { PageHeader, Kpi, Panel, Loading, Empty, ExportBtn, FilterBar, Field, NSelect, useFetch } from "@/components/common";
import { Input } from "@/components/ui/input";
import { TxTable } from "@/pages/commerciaux/Mooncard";
import { CardsTable } from "@/components/FuelCards";
import { useAuth } from "@/context/AuthContext";
import { fEur, fNum, fMonth } from "@/lib/api";

const MONTHS = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];

function Filters({ f, setF, cards }) {
  const v = useFetch("/vehicles", { type: "livraison" });
  const set = (k) => (x) => setF({ ...f, [k]: x });
  const y = new Date().getFullYear();
  return (
    <FilterBar testid="logi-fuel-filters">
      <Field label="Titulaire"><NSelect value={f.card_id} onChange={set("card_id")} placeholder="Tous" testid="logi-filter-holder" options={cards.map((c) => ({ value: c.id, label: c.holder_name }))} /></Field>
      <Field label="Véhicule"><NSelect value={f.vehicle_id} onChange={set("vehicle_id")} placeholder="Tous" testid="logi-filter-vehicle" options={(v.data || []).filter((x) => !x.is_demo).map((x) => ({ value: x.id, label: x.plate }))} /></Field>
      <Field label="Année"><NSelect value={f.year} onChange={set("year")} placeholder="Toutes" testid="logi-filter-year" options={[y, y - 1].map((x) => ({ value: String(x), label: String(x) }))} /></Field>
      <Field label="Mois"><NSelect value={f.month} onChange={(m) => setF({ ...f, month: m, year: f.year || (m ? String(y) : "") })} placeholder="Tous" testid="logi-filter-month" options={MONTHS.map((m, i) => ({ value: String(i + 1), label: m }))} /></Field>
      <Field label="Du"><Input type="date" value={f.date_from || ""} onChange={(e) => set("date_from")(e.target.value)} className="h-10 bg-white" data-testid="logi-filter-from" /></Field>
      <Field label="Au"><Input type="date" value={f.date_to || ""} onChange={(e) => set("date_to")(e.target.value)} className="h-10 bg-white" data-testid="logi-filter-to" /></Field>
      <div className="flex items-end"><button onClick={() => setF({})} className="h-10 px-3 text-sm font-semibold text-slate-500 hover:text-[#0E2D43]" data-testid="logi-filter-reset">Réinitialiser</button></div>
    </FilterBar>
  );
}

export default function LogiCarburant() {
  const { user } = useAuth();
  const admin = user.role === "admin";
  const [f, setF] = useState({});
  const { data, loading } = useFetch("/logistique/fuel", f);
  const k = data?.kpis;
  const extra = Object.fromEntries((data?.cards || []).map((c) => [c.id, c]));
  return (
    <div data-testid="logi-fuel-page">
      <PageHeader overline="Véhicules de livraison" title="Cartes carburant Fleet Pro">
        {user.real_role !== "direction" && <Link to="/livraison/plein" className="h-10 px-4 inline-flex items-center rounded-md bg-[#F49619] hover:bg-[#E0830B] text-white text-sm font-semibold transition-colors" data-testid="logi-plein-cta">Enregistrer un plein Fleet Pro</Link>}
        {admin && <ExportBtn path="/export/logistique/fuel" params={f} filename="carburant-logistique.xlsx" testid="export-logi-fuel" />}
      </PageHeader>
      {!admin && <p className="text-sm text-slate-500 mb-4">Vous voyez uniquement votre carte et vos transactions. Les paramètres des cartes sont gérés par la Direction.</p>}
      <div className="bg-white border border-slate-200 rounded-xl mb-6"><CardsTable kind="logistique" extra={extra} testid="logi-cards" /></div>
      <Filters f={f} setF={setF} cards={data?.cards || []} />
      {loading || !data ? <Loading /> : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
            <Kpi testid="logi-kpi-spend" label="Dépenses TTC" value={fEur(k.spend, 0)} sub={`HT : ${fEur(k.spend_ht, 0)}`} icon={Euro} tone="navy" />
            <Kpi testid="logi-kpi-liters" label="Litres" value={`${fNum(k.liters, 0)} L`} icon={Droplet} delay={1} />
            <Kpi testid="logi-kpi-ppl" label="Prix moyen pondéré" value={k.ppl ? `${fNum(k.ppl, 3)} €/L` : "—"} icon={Fuel} delay={2} />
            <Kpi testid="logi-kpi-count" label="Transactions" value={k.count} icon={Receipt} delay={3} />
          </div>
          <Panel title="Dépenses mensuelles" className="mb-6" testid="logi-chart-monthly">
            {!data.monthly.length ? <Empty /> : (
              <div className="h-60"><ResponsiveContainer><BarChart data={data.monthly}><CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                <XAxis dataKey="month" tickFormatter={fMonth} tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} width={60} />
                <Tooltip labelFormatter={fMonth} formatter={(x) => fEur(x)} /><Bar dataKey="spend" name="Dépenses TTC" fill="#2F80ED" radius={[4, 4, 0, 0]} /></BarChart></ResponsiveContainer></div>
            )}
          </Panel>
          <div className="bg-white border border-slate-200 rounded-xl" data-testid="logi-tx">
            {!data.transactions.length ? <Empty text={admin ? "Aucune transaction Fleet Pro logistique — importez un export Fleet Pro (Véhicules commerciaux → Carburant → Imports)" : "Aucune transaction sur votre carte pour cette période"} /> : <TxTable rows={data.transactions} userLabel="Titulaire" />}
          </div>
        </>
      )}
    </div>
  );
}
